import { inject, Injector } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, PermissionService, UsersMeService } from '@zag/auth/ui';
import { ToastService } from '@zag/ui';
import { TenantStatusService } from './tenant-status.service';

const OVERVIEW_PATH = '/time-tracking/overview';

function isOverviewQueryChange(currentUrl: string, targetUrl: string): boolean {
  const current = currentUrl.split('#')[0] ?? '';
  const target = targetUrl.split('#')[0] ?? '';
  return current.split('?')[0] === OVERVIEW_PATH && target.split('?')[0] === OVERVIEW_PATH && current !== target;
}

export const overviewDraftNavigationGuard: CanActivateFn = async (_route, state) => {
  const router = inject(Router);
  const injector = inject(Injector);
  const auth = inject(AuthService);
  const permissions = inject(PermissionService);
  const usersMe = inject(UsersMeService);
  const tenant = inject(TenantStatusService);
  const translate = inject(TranslateService);
  const toast = inject(ToastService);

  if (!isOverviewQueryChange(router.routerState.snapshot.url, state.url)) return true;

  const canBlockNavigation = () =>
    auth.isAuthenticated() &&
    !auth.isSigningOut() &&
    permissions.isLoaded() &&
    !permissions.hasError() &&
    !permissions.hasNoPermissions() &&
    permissions.hasPermission('timetracking:overview:read') &&
    !usersMe.profileIsLoading() &&
    !usersMe.profileError() &&
    usersMe.profile() !== undefined &&
    !usersMe.profile()?.mustChangePassword &&
    !tenant.requiresProvisioning() &&
    !tenant.isUnderMaintenance() &&
    !tenant.isDeprovisioned();

  if (!canBlockNavigation()) return true;
  const userId = auth.currentUser()?.id;
  if (!userId) return true;
  const tenantId = tenant.host || 'local';

  const { OverviewFacadeService } = await import('@zag/timetracking/ui');
  if (!canBlockNavigation() || auth.currentUser()?.id !== userId || (tenant.host || 'local') !== tenantId) return true;

  const facade = injector.get(OverviewFacadeService);
  if (!facade.matchesWorkspaceContext({ workspaceId: 'time-tracking/overview', userId, tenantId })) return true;
  if (!facade.navigationGuard().blocked) return true;

  toast.warning(translate.instant('overview.balance.navigationBlocked'));
  return false;
};
